// Port-local time, without a time-zone library. Ferry speaks UTC (`Z`) and
// tells us each port's IANA zone; the operator thinks in the port's clock.
// `Intl` can render an instant in a zone but not parse one, so the reverse
// direction is done by measuring the zone's offset at the guessed instant and
// correcting once more for a DST boundary.

/** The zone's wall clock at `ms`, re-encoded as a UTC timestamp (so two can be subtracted). */
function wallClockAsUtcMs(ms: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
}

/** `YYYY-MM-DD` + `HH:mm` on a port's clock → the UTC instant as an ISO string with `Z`. */
export function zonedToUtc(date: string, time: string, zone: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wall = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0);
  let guess = wall - (wallClockAsUtcMs(wall, zone) - wall);
  const offsetAtGuess = wallClockAsUtcMs(guess, zone) - guess;
  if (wall - offsetAtGuess !== guess) guess = wall - offsetAtGuess;
  return new Date(guess).toISOString().replace('.000Z', 'Z');
}

/** A UTC instant on a port's clock, split for form fields: `{ date: 'YYYY-MM-DD', time: 'HH:mm' }`. */
export function utcToZoned(iso: string, zone: string): { date: string; time: string } {
  const ms = new Date(iso).getTime();
  const wall = new Date(wallClockAsUtcMs(ms, zone));
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}`,
    time: `${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}`,
  };
}

/** `HH:mm` of a UTC instant on a port's clock. */
export function formatPortTime(
  iso: string | null | undefined,
  zone: string | null | undefined,
): string {
  if (!iso) return '';
  return utcToZoned(iso, zone || 'UTC').time;
}

/** The UTC bounds of one calendar day on a port's clock — the window to ask Ferry for that day's sailings. */
export function portDayBoundsUtc(date: string, zone: string): { fromUtc: string; toUtc: string } {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  const pad = (n: number) => String(n).padStart(2, '0');
  const nextDate = `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
  return { fromUtc: zonedToUtc(date, '00:00', zone), toUtc: zonedToUtc(nextDate, '00:00', zone) };
}
